import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from '@api';
import { useTrans } from '@libs/i18n';
import {
  GetDocumentDownloadUrlParam,
  GetDocumentDownloadUrlResponse,
} from '@libs/types';

import { getLocalizedErrorMessage } from '../utils/error-utils';

// Service functions

async function getDocumentDownloadUrl(
  params: GetDocumentDownloadUrlParam,
): Promise<GetDocumentDownloadUrlResponse> {
  const response = await apiClient.get<GetDocumentDownloadUrlResponse>(
    `api/assessments/documents/${params.documentId}/download-url`,
  );

  return response.data;
}

// React Query hooks
export const useGetDocumentDownloadUrl = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();
  return useMutation<GetDocumentDownloadUrlResponse, Error, GetDocumentDownloadUrlParam>({
    mutationFn: getDocumentDownloadUrl,
    onSuccess: (data) => {
      // Open the presigned URL in a new tab to trigger download
      window.open(data.s3DownloadUrl, '_blank');

      enqueueSnackbar(t('app.assessment.document.downloadStarted'), {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000,
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to download document. Please try again.');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 6000,
      });
    },
  });
};
